import { queryClient } from '~/api/queryClient';
import { queryKeys } from '~/api/queryKeys';
import { useCreateExperimentStore } from '~/features/experiments/store/create-experiment.store';
import { useExperimentCreateOriginStore } from '~/features/experiments/store/experiment-create-origin.store';
import { useAuthStore } from '~/store/auth.store';

import { logout } from '../services/auth.service';

/**
 * Single source of truth for the logout sequence. Runs the backend
 * `/auth/logout` (best-effort) and then wipes every piece of user-scoped
 * client state that the SPA owns: auth tokens, the create-experiment
 * wizard (sessionStorage-backed) and its cross-flow origin flag, the
 * cached tenant theme and the per-caller cost queries. Does NOT clear locale/preferences stores —
 * those are machine-level and intentionally outlive the session. Callers
 * are responsible for navigation after this resolves.
 */
export async function performLogout(): Promise<void> {
  try {
    await logout();
  } catch {
    // Backend revoke can fail (offline, 5xx) — proceed with local cleanup
    // anyway so the UI doesn't get stuck in a half-authenticated state.
  }

  useAuthStore.getState().clearUser();

  // Cost reads are per caller (who may see amounts, approver status): drop them so the next
  // user on this tab starts from their own answers, never the last user's.
  queryClient.removeQueries({ queryKey: queryKeys.costEstimates.all });
  queryClient.removeQueries({ queryKey: queryKeys.catalogService.costEstimates.all() });

  // Wizard state — reset in-memory slice AND wipe the persisted
  // sessionStorage entry so a different user signing in next sees a
  // clean wizard.
  useCreateExperimentStore.getState().reset();
  useCreateExperimentStore.persist.clearStorage();

  // In-memory cross-flow redirect flag — wouldn't survive a reload but
  // logout is an SPA nav, so clear it explicitly.
  useExperimentCreateOriginStore.getState().clearOriginChannel();

  // Tenant-specific theme cached for FOUC prevention (see main.tsx).
  try {
    sessionStorage.removeItem('tenant-theme');
  } catch {
    // sessionStorage unavailable — non-fatal.
  }
}
